import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import ScaleIcon from '@mui/icons-material/ScaleOutlined';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import PrintDisabledIcon from '@mui/icons-material/PrintDisabledOutlined';
import SensorsIcon from '@mui/icons-material/SensorsOutlined';
import SensorsOffIcon from '@mui/icons-material/SensorsOffOutlined';
import ComputerIcon from '@mui/icons-material/ComputerOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { useDateFormat } from '@/hooks/useDateFormat';
import type { HardwareStationStatusDto } from '@/types/weightProduction';

/** Matches the agent's own cadence closely enough that a station going down shows up within seconds
 * of the server deciding it is stale, without hammering the API. */
const REFRESH_MS = 10_000;

function secondsAgo(iso: string | null): number | null {
  if (!iso) return null;
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
}

function LastSeen({ iso }: { iso: string | null }) {
  const { t } = useTranslation('weightProduction');
  const seconds = secondsAgo(iso);
  if (seconds === null) return <>{t('monitor.neverReported')}</>;
  if (seconds < 60) return <>{t('monitor.secondsAgo', { count: seconds })}</>;
  if (seconds < 3600) return <>{t('monitor.minutesAgo', { count: Math.round(seconds / 60) })}</>;
  return <>{t('monitor.hoursAgo', { count: Math.round(seconds / 3600) })}</>;
}

/**
 * Fleet view of every weighing station — which PC, which area, and whether its scale and printer are
 * actually working right now.
 *
 * Everything here comes from heartbeats the agents push to the API; nothing is polled from the
 * stations directly, because a browser can only reach its own machine's agent on 127.0.0.1. The
 * consequence worth understanding: a station that has stopped reporting cannot be distinguished from
 * a healthy one by its stored fields alone, so the server marks it offline on staleness and this
 * screen leads with that.
 */
export default function HardwareMonitorPage() {
  const { t } = useTranslation('weightProduction');
  const { formatDateTime } = useDateFormat();

  const { data: stations = [], isLoading } = useQuery({
    queryKey: ['hardware-monitor'],
    queryFn: async () => (await apiClient.get<HardwareStationStatusDto[]>('/hardware-monitor')).data,
    refetchInterval: REFRESH_MS,
  });

  const online = stations.filter((s) => s.agentOnline).length;
  const withProblems = stations.filter((s) => s.agentOnline && (!s.serialConnected || !s.printerOnline)).length;

  return (
    <>
      <PageHeader title={t('monitor.title')} subtitle={t('monitor.subtitle')} />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Chip color="success" variant="outlined" label={t('monitor.summaryOnline', { count: online, total: stations.length })} />
        {withProblems > 0 && <Chip color="warning" label={t('monitor.summaryProblems', { count: withProblems })} />}
        <Chip variant="outlined" label={t('monitor.autoRefresh', { seconds: REFRESH_MS / 1000 })} />
      </Stack>

      {!isLoading && stations.length === 0 && (
        <Alert severity="info">{t('monitor.noStations')}</Alert>
      )}

      <GridContainer columns={12} spacing={2}>
        {stations.map((station) => {
          const scaleOk = station.agentOnline && station.serialConnected;
          const printerOk = station.agentOnline && station.printerOnline;
          const accent = !station.agentOnline ? 'error' : !scaleOk || !printerOk ? 'warning' : 'success';

          return (
            <GridItem key={station.hardwareStationId} xs={12} md={6} lg={4}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%', borderRadius: 3, borderWidth: 2,
                  borderColor: (theme) => theme.palette[accent].main,
                  bgcolor: (theme) => alpha(theme.palette[accent].main, 0.04),
                }}
              >
                <CardContent>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800 }} noWrap>{station.stationName}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {station.stationCode}{station.plantArea ? ` · ${station.plantArea}` : ''}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={station.agentOnline ? 'success' : 'error'}
                      icon={station.agentOnline ? <SensorsIcon /> : <SensorsOffIcon />}
                      label={station.agentOnline ? t('monitor.agentOnline') : t('monitor.agentOffline')}
                      sx={{ fontWeight: 700 }}
                    />
                  </Stack>

                  {/* A station with no key can never report — that is a setup gap, not a fault. */}
                  {!station.hasAgentKey && (
                    <Alert severity="info" sx={{ mt: 1.5, py: 0.25 }}>{t('monitor.noAgentKey')}</Alert>
                  )}

                  <Stack spacing={1} sx={{ mt: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ScaleIcon fontSize="small" color={scaleOk ? 'success' : 'disabled'} />
                      <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
                        {station.serialPort ?? '—'}
                        {station.lastWeight !== null && scaleOk ? ` · ${station.lastWeight}` : ''}
                      </Typography>
                      <Chip
                        size="small"
                        variant={scaleOk ? 'filled' : 'outlined'}
                        color={scaleOk ? 'success' : station.serialReleased ? 'default' : 'error'}
                        label={scaleOk ? t('monitor.scaleConnected') : station.serialReleased ? t('monitor.scaleReleased') : t('monitor.scaleDown')}
                      />
                    </Stack>
                    {station.agentOnline && station.serialLastError && (
                      <Typography variant="caption" color="error.main" noWrap>{station.serialLastError}</Typography>
                    )}

                    <Stack direction="row" spacing={1} alignItems="center">
                      {printerOk ? <PrintIcon fontSize="small" color="success" /> : <PrintDisabledIcon fontSize="small" color="disabled" />}
                      <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>{station.printerName ?? '—'}</Typography>
                      <Tooltip title={station.printerProblem ?? station.printerStatus ?? ''}>
                        <Chip
                          size="small"
                          variant={printerOk ? 'filled' : 'outlined'}
                          color={printerOk ? 'success' : 'error'}
                          label={printerOk ? (station.printerStatus ?? t('monitor.printerReady')) : t('monitor.printerDown')}
                        />
                      </Tooltip>
                    </Stack>
                    {station.agentOnline && station.printerQueuedJobs > 0 && (
                      <Typography variant="caption" color="warning.main">
                        {t('monitor.queuedJobs', { count: station.printerQueuedJobs })}
                      </Typography>
                    )}

                    <Stack direction="row" spacing={1} alignItems="center">
                      <ComputerIcon fontSize="small" color="disabled" />
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ flexGrow: 1 }}>
                        {station.machineName ?? '—'}{station.ipAddress ? ` · ${station.ipAddress}` : ''}
                        {station.agentVersion ? ` · v${station.agentVersion}` : ''}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('monitor.todayWeighings')}</Typography>
                      <Typography sx={{ fontWeight: 700 }}>{station.weighingsToday}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('monitor.todayLabels')}</Typography>
                      <Typography sx={{ fontWeight: 700 }}>{station.labelsPrintedToday}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">{t('monitor.lastSeen')}</Typography>
                      <Tooltip title={station.lastHeartbeatAt ? formatDateTime(station.lastHeartbeatAt) : ''}>
                        <Typography sx={{ fontWeight: 700 }} noWrap><LastSeen iso={station.lastHeartbeatAt} /></Typography>
                      </Tooltip>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </GridItem>
          );
        })}
      </GridContainer>
    </>
  );
}
